import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiRadio, FiClock, FiCalendar, FiArrowRight, FiUsers } = FiIcons;

// ── Countdown Hook ────────────────────────────────────────────────────────────
function useCountdown(targetDate) {
  const calc = () => {
    const diff = new Date(targetDate) - Date.now();
    if (diff <= 0) return null;
    return {
      hours:   Math.floor(diff / 3600000),
      minutes: Math.floor((diff % 3600000) / 60000),
      seconds: Math.floor((diff % 60000) / 1000),
    };
  };
  const [time, setTime] = useState(calc);
  useEffect(() => {
    const id = setInterval(() => setTime(calc()), 1000);
    return () => clearInterval(id);
  }, [targetDate]);
  return time;
}

// ── Digit Block ───────────────────────────────────────────────────────────────
const Digit = ({ value, label }) => (
  <div className="flex flex-col items-center">
    <div className="bg-white/20 backdrop-blur-sm border border-white/30 rounded-xl px-3 py-1.5 min-w-[48px] text-center">
      <span className="text-xl sm:text-2xl font-black text-white tabular-nums leading-none">
        {String(value).padStart(2, '0')}
      </span>
    </div>
    <span className="text-[9px] font-bold uppercase tracking-widest text-white/70 mt-1">{label}</span>
  </div>
);

// ── Live Now Banner ───────────────────────────────────────────────────────────
const LiveNowBanner = ({ liveCount, totalViewers }) => (
  <div className="flex flex-col sm:flex-row items-center justify-between gap-4 w-full">
    <div className="flex items-center gap-4">
      <div className="relative flex-shrink-0">
        <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
          <SafeIcon icon={FiRadio} className="w-5 h-5 text-white" />
        </div>
        <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-400 rounded-full animate-ping" />
        <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full" />
      </div>
      <div>
        <p className="text-white font-black text-lg sm:text-xl leading-tight">
          {liveCount} Sanctuary{liveCount !== 1 ? 'ies' : ''} Live Right Now
        </p>
        <p className="text-white/70 text-xs font-medium">
          Join a remembrance service currently in progress
        </p>
      </div>
    </div>
    <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm border border-white/30 rounded-full px-4 py-2">
      <SafeIcon icon={FiUsers} className="w-3.5 h-3.5 text-white/80" />
      <span className="text-white font-bold text-sm">{totalViewers.toLocaleString()} watching</span>
    </div>
  </div>
);

// ── Countdown Banner ──────────────────────────────────────────────────────────
const CountdownBanner = ({ nextEvent, countdown }) => (
  <div className="flex flex-col sm:flex-row items-center justify-between gap-6 w-full">
    <div className="flex items-center gap-4">
      <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
        <SafeIcon icon={FiCalendar} className="w-5 h-5 text-white" />
      </div>
      <div>
        <p className="text-white/70 text-[10px] font-black uppercase tracking-widest mb-0.5">
          Next Service Starting In
        </p>
        <p className="text-white font-bold text-base sm:text-lg leading-tight line-clamp-1">
          {nextEvent.title}
        </p>
        <p className="text-white/60 text-xs">Hosted by {nextEvent.organizer}</p>
      </div>
    </div>
    {countdown ? (
      <div className="flex items-center gap-2 flex-shrink-0">
        <Digit value={countdown.hours}   label="hrs"  />
        <span className="text-white/60 font-black text-xl mb-4">:</span>
        <Digit value={countdown.minutes} label="min"  />
        <span className="text-white/60 font-black text-xl mb-4">:</span>
        <Digit value={countdown.seconds} label="sec"  />
      </div>
    ) : (
      <span className="text-white/60 text-sm">Starting soon…</span>
    )}
  </div>
);

// ── No Events Banner ──────────────────────────────────────────────────────────
const NoEventsBanner = ({ onExplore }) => (
  <div className="flex flex-col sm:flex-row items-center justify-between gap-4 w-full">
    <div className="flex items-center gap-4">
      <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
        <SafeIcon icon={FiClock} className="w-5 h-5 text-white" />
      </div>
      <div>
        <p className="text-white font-black text-lg leading-tight">No Live Events Currently</p>
        <p className="text-white/70 text-xs font-medium">
          All sanctuaries are quiet right now — check back soon or explore upcoming sessions.
        </p>
      </div>
    </div>
    <button
      onClick={onExplore}
      className="flex-shrink-0 flex items-center gap-2 bg-white/20 hover:bg-white/30 backdrop-blur-sm border border-white/30 text-white text-xs font-black uppercase tracking-widest px-5 py-2.5 rounded-full transition-all duration-200"
    >
      Explore Memorials <SafeIcon icon={FiArrowRight} className="w-3.5 h-3.5" />
    </button>
  </div>
);

// ── Main Banner ───────────────────────────────────────────────────────────────
const StatusBanner = ({ streams = [], scheduledEvents = [] }) => {
  const navigate = useNavigate();

  const liveStreams   = streams.filter(s => s.isLive);
  const totalViewers  = liveStreams.reduce((sum, s) => sum + (s.viewers || 0), 0);
  const hasLive       = liveStreams.length > 0;

  // Find the soonest upcoming scheduled event
  const now = Date.now();
  const upcoming = scheduledEvents
    .filter(e => new Date(e.date) > now)
    .sort((a, b) => new Date(a.date) - new Date(b.date));
  const nextEvent = upcoming[0] || null;

  const countdown = useCountdown(nextEvent?.date);

  // Determine mode
  const mode = hasLive ? 'live' : nextEvent ? 'countdown' : 'empty';

  const gradients = {
    live:      'from-red-700 via-red-600 to-rose-500',
    countdown: 'from-accent-navy via-blue-800 to-indigo-700',
    empty:     'from-slate-700 via-slate-600 to-slate-500',
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
      className={`w-full rounded-[28px] bg-gradient-to-r ${gradients[mode]} shadow-2xl px-6 sm:px-10 py-5 mb-10 overflow-hidden relative`}
    >
      {/* Decorative glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-10 -left-10 w-48 h-48 bg-white/5 rounded-full blur-3xl" />
        <div className="absolute -bottom-10 -right-10 w-64 h-64 bg-white/5 rounded-full blur-3xl" />
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={mode}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.35 }}
          className="relative z-10"
        >
          {mode === 'live' && (
            <LiveNowBanner liveCount={liveStreams.length} totalViewers={totalViewers} />
          )}
          {mode === 'countdown' && (
            <CountdownBanner nextEvent={nextEvent} countdown={countdown} />
          )}
          {mode === 'empty' && (
            <NoEventsBanner onExplore={() => navigate('/explore')} />
          )}
        </motion.div>
      </AnimatePresence>
    </motion.div>
  );
};

export default StatusBanner;